import { type ParsedEvent, createParser } from 'eventsource-parser'
import { get } from 'lodash-es'

import { getBasicStream } from '../../helpers/stream'

import { streamAsyncIterable } from './stream-async-iterable'

export interface IFetchSSEOptions extends RequestInit {
  /**
   * fetch 请求完成后回调
   *
   * 值得注意的是，fetch 默认不会抛出异常，可根据 response.ok 判断请求是否成功
   */
  onFetchResponse?: (response: Response) => void | Promise<void>
  /**
   * 可在消息反序列化之前处理原始 message 字符串
   */
  beforeParseEvent?: (messageStr: string) => void | Promise<void>
  onEvent: (parsedEvent: ParsedEvent) => void
  /**
   * 支持返回 mock 数据，仅在开发环境有效，内置默认文本，也可自定义返回内容
   *
   * 此时只有 onEvent 会被正常调用
   */
  mockResponse?:
    | boolean
    | {
        content: string
      }
}

/** SSE 核心封装，仅做核心 SSE 请求逻辑处理，不涉及任何其他业务 */
export async function fetchSSE(resource: string, options: IFetchSSEOptions) {
  const {
    onEvent,
    beforeParseEvent,
    onFetchResponse,
    mockResponse,
    ...fetchOptions
  } = options

  const parser = createParser((event) => {
    /**
     * 常规 SSE 流数据生成结果，event 为空，形如：
     *
     * {type: 'event', id: undefined, event: undefined, data: '{"content":"Hello"}'}
      */
    if (event.type === 'event') {
      onEvent(event)
    }
  })

  if (mockResponse) {
    const content = get(mockResponse, 'content')
    await getBasicStream(content)(parser.feed)
    return
  }

  const response = await fetch(resource, fetchOptions)
  await onFetchResponse?.(response)
  if (response.ok) {
    for await (const chunk of streamAsyncIterable(response.body!)) {
      const str = new TextDecoder().decode(chunk)
      await beforeParseEvent?.(str)
      parser.feed(str)
    }
  }
}
